// @givmo/board — the board's self-assessment (CBG-YR4-ASSESS-WEB; frames s1 and s3, and the dashboard
// no frame drew): the Self-assessment page's one part (`evaluations-page.tsx`).
//
// ══ WHAT THE PART SHOWS, AND WHEN ═══════════════════════════════════════════════════════════════
//
//   * ONE OPEN → its dashboard: who was asked and who has answered — plain words, never what anyone
//     answered (D-CBG-28) — with the officer's acts (Send, Remind, a paper sheet and its mark, Print
//     the questions, Close, Withdraw before any answer).
//   * NONE OPEN → one card with "Start the {next} self-assessment" for an officer, and the earlier
//     ones, each a link to its results (s1's page behind the dialog).
//   * `?assessment=<id>` → that one: a closed one's results with the board's priorities (s3, which
//     the design drew at an address of its own too), a withdrawn one's one line, an open one's
//     dashboard. "All self-assessments" above it is the way back, which the design's tab strip gave.
//   * NOT SERVED (the flag off: the list read is the route miss) → one quiet line. An organization of
//     a type Givmo offers no starting self-assessment yet reads the backend's own sentence and no Start.
//
// ONE COUNT, OF ANSWER SETS, in words ("3 of 7 answered"), with no bar. Nothing is "overdue" or "late"
// after the due date: the date simply stays. Nothing schedules anything; reminders go when an officer
// presses Remind. Every act is `board_record:approve` (contract C1): a reader without it sees the
// facts and the results, and is offered no act at all.
//
// The part is its own component, with no page frame of its own: CBG-YR4-PAY's web half put it in a tab
// beside Compensation decisions without changing it, and CBG-UX8-MENU took that tab away again when
// the decisions moved to the Form 990 page (`evaluations-page.tsx`).

import { useState } from 'react';
import { Mail, MoreHorizontal, Send } from 'lucide-react';
import type { BoardSelfAssessmentOut, BoardSelfAssessmentPersonOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  Icon,
  IconButton,
  Menu,
  RowActions,
  Skeleton,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { Link, useNavigate, useSearch } from '../../board-routing';
import { BOARD_PAGES } from '../../board-routes';
import { GroupLabel, HowThisWorks, StatusWord } from '../../components/fine-print';
import { displayDate } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
import { PersonCell } from '../conflicts/coi-parts';
import {
  canWithdraw,
  earlierAssessments,
  earlierLine,
  nextLabel,
  openAssessmentOf,
  personCapacity,
  personStatus,
  remindablePeople,
  sendablePeople,
  sheetsWaiting,
} from './assessment-decode';
import {
  CloseDialog,
  PaperSheetDialog,
  PrioritiesDialog,
  RemindDialog,
  SendDialog,
  WithdrawDialog,
} from './assessment-dialogs';
import { useQuestionsPrint } from './assessment-print';
import {
  useRefreshSelfAssessments,
  useSelfAssessment,
  useSelfAssessmentResults,
  useSelfAssessments,
  useStartingInstrument,
} from './assessment-queries';
import { AssessmentResults } from './assessment-results';
import { AssessmentStartDialog } from './assessment-start-dialog';
import { assessmentWords as w } from './assessment-words';

/** The act each officer control runs: the one string every act of the aggregate rides (C1). */
export const ASSESSMENT_ACT_PERMISSION = 'board_record:approve';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const SMALL_LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
/** A link inside a line of facts: the line's own weight, so it reads as part of the sentence. */
const INLINE_LINK =
  'font-sans text-s13 font-w400 tracking-givmo text-brand-600 underline-offset-2 outline-none transition-colors hover:text-brand-700 hover:underline focus-visible:underline';

/** The search value, when it is a non-empty string. */
function textParam(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** A failed read: one line and "Try again". */
function ReadFailed({ onRetry, testId }: { onRetry: () => void; testId: string }) {
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col items-start gap-12">
        <p className={SECONDARY}>{w.loadFailed}</p>
        <Button appearance="saas" tone="secondary" onClick={onRetry} testId={`${testId}-retry`}>
          {w.tryAgain}
        </Button>
      </CardBody>
    </Card>
  );
}

export function SelfAssessmentPart() {
  const { orgRef, isReady, orgName } = useBoardHost();
  const search = useSearch();
  const selected = textParam(search.assessment);
  const listQuery = useSelfAssessments(orgRef, isReady);
  const list = listQuery.data;
  const organization = orgName?.trim() || null;

  if (!isReady || orgRef === null || listQuery.isPending) {
    return (
      <div className="flex flex-col gap-12" data-testid="assessment-loading">
        <Skeleton className="h-84 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }
  if (listQuery.isError || list === undefined) {
    return <ReadFailed onRetry={() => void listQuery.refetch()} testId="assessment-error" />;
  }
  if (list === null) {
    return (
      <Card testId="assessment-not-served">
        <CardBody>
          <p className={SECONDARY}>{w.notServed}</p>
        </CardBody>
      </Card>
    );
  }

  const chosen = selected === null ? null : (list.find((row) => row.id === selected) ?? null);
  if (chosen !== null && chosen.status === 'closed') {
    return <ResultsView orgRef={orgRef} assessmentId={chosen.id} />;
  }
  if (chosen !== null && chosen.status === 'cancelled') {
    return (
      <div className="flex flex-col gap-16" data-testid="assessment-withdrawn">
        <BackToAll />
        <Card>
          <CardBody>
            <p className={SECONDARY}>{w.withdrawn(chosen.label)}</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  const open = openAssessmentOf(list);
  const earlier = earlierAssessments(list);
  return (
    <div className="flex flex-col gap-24" data-testid="assessment-part">
      {open !== null ? (
        <OpenDashboard orgRef={orgRef} assessmentId={open.id} organization={organization} />
      ) : (
        <RunCard orgRef={orgRef} label={nextLabel(list)} organization={organization} />
      )}
      {earlier.length > 0 ? (
        <section className="flex flex-col gap-8" data-testid="assessment-earlier">
          <GroupLabel>{w.earlier}</GroupLabel>
          <ul className="flex flex-col gap-6">
            {earlier.map((row) => (
              <li key={row.id}>
                {row.status === 'closed' ? (
                  <Link
                    to={BOARD_PAGES.evaluations}
                    search={{ assessment: row.id }}
                    exact
                    className={LINK}
                    data-testid={`assessment-earlier-${row.id}`}
                  >
                    {earlierLine(row)}
                  </Link>
                ) : (
                  <span
                    className="font-sans text-s14 font-w400 tracking-givmo text-text-tertiary"
                    data-testid={`assessment-earlier-${row.id}`}
                  >
                    {earlierLine(row)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** The way back from one assessment to the part's own view. */
function BackToAll() {
  return (
    <Link
      to={BOARD_PAGES.evaluations}
      exact
      className={`${SMALL_LINK} self-start`}
      data-testid="assessment-back"
    >
      {w.allSelfAssessments}
    </Link>
  );
}

/** None open: what the self-assessment is, and the start for an officer (s1's page). */
function RunCard({
  orgRef,
  label,
  organization,
}: {
  orgRef: string;
  label: string;
  organization: string | null;
}) {
  const canAct = useBoardPermission(ASSESSMENT_ACT_PERMISSION);
  const instrumentQuery = useStartingInstrument(orgRef);
  const [starting, setStarting] = useState(false);
  // A type offered no starting self-assessment yet (the backend's §3.1): its own sentence.
  const notOffered = instrumentQuery.isSuccess && instrumentQuery.data === null;

  return (
    <Card testId="assessment-run">
      <CardBody className="flex flex-col items-start gap-12">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {w.runTitle}
          </h2>
          <p className={`max-w-[64ch] ${SECONDARY}`}>{w.runLine}</p>
        </div>
        {notOffered ? (
          <p className={SECONDARY} data-testid="assessment-not-offered">
            {w.notOffered}
          </p>
        ) : canAct ? (
          <Button
            appearance="saas"
            tone="primary"
            disabled={!instrumentQuery.isSuccess}
            onClick={() => setStarting(true)}
            testId="assessment-start"
          >
            {w.start(label)}
          </Button>
        ) : null}
      </CardBody>
      {starting ? (
        <AssessmentStartDialog
          orgRef={orgRef}
          label={label}
          organization={organization}
          onClose={() => setStarting(false)}
        />
      ) : null}
    </Card>
  );
}

type Open =
  | { kind: 'send'; people: BoardSelfAssessmentPersonOut[] }
  | { kind: 'remind'; people: BoardSelfAssessmentPersonOut[] }
  | { kind: 'close' }
  | { kind: 'withdraw' }
  | { kind: 'sheet' }
  | null;

/** The open one: who was asked, who has answered (never what), and the officer's acts. */
function OpenDashboard({
  orgRef,
  assessmentId,
  organization,
}: {
  orgRef: string;
  assessmentId: string;
  organization: string | null;
}) {
  const canAct = useBoardPermission(ASSESSMENT_ACT_PERMISSION);
  const navigate = useNavigate();
  const query = useSelfAssessment(orgRef, assessmentId);
  const assessment = query.data ?? null;
  const [open, setOpen] = useState<Open>(null);
  const { print, sheet } = useQuestionsPrint(orgRef, organization);

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-12" data-testid="assessment-open-loading">
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-84 w-full rounded-lg" />
      </div>
    );
  }
  if (assessment === null) {
    return <ReadFailed onRetry={() => void query.refetch()} testId="assessment-open-error" />;
  }

  const remindable = remindablePeople(assessment.people);
  const sendable = sendablePeople(assessment.people);
  const waiting = sheetsWaiting(assessment);
  const withdrawable = canWithdraw(assessment);

  return (
    <section className="flex flex-col gap-16" data-testid="assessment-open">
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="flex min-w-0 flex-col gap-4">
          <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {w.title(assessment.label)}
          </h2>
          <p className={SECONDARY} data-testid="assessment-open-facts">
            {w.openFacts(
              displayDate(assessment.due_on),
              shortDay(assessment.opened_at ?? null),
              assessment.opened_by_display_name ?? null,
            )}
            {'. '}
            <HowThisWorks testId="assessment-open-how" lines={w.openHow} />
          </p>
        </div>
        {canAct ? (
          <div className="flex items-center gap-8">
            {remindable.length > 0 ? (
              <Button
                appearance="saas"
                tone="secondary"
                leadingIcon={<Icon icon={Mail} size={16} aria-hidden />}
                onClick={() => setOpen({ kind: 'remind', people: remindable })}
                testId="assessment-remind"
              >
                {w.remindPeople(remindable.length)}
              </Button>
            ) : null}
            <Menu.Root>
              <Menu.Trigger
                render={
                  <IconButton
                    aria-label={w.menuFor(assessment.label)}
                    tone="secondary"
                    size="md"
                    testId="assessment-menu"
                  />
                }
              >
                <Icon icon={MoreHorizontal} size={18} aria-hidden />
              </Menu.Trigger>
              <Menu.Content
                saas
                align="end"
                className="min-w-[248px]"
                testId="assessment-menu-content"
              >
                {sendable.length > 1 ? (
                  <Menu.Item
                    saas
                    onClick={() => setOpen({ kind: 'send', people: sendable })}
                    testId="assessment-send-all"
                  >
                    {w.sendPeople(sendable.length)}
                  </Menu.Item>
                ) : null}
                {waiting > 0 ? (
                  <Menu.Item
                    saas
                    onClick={() => setOpen({ kind: 'sheet' })}
                    testId="assessment-sheet"
                  >
                    {w.recordSheet}
                  </Menu.Item>
                ) : null}
                <Menu.Item saas onClick={() => print(assessment.id)} testId="assessment-print">
                  {w.printQuestions}
                </Menu.Item>
                <Menu.Item
                  saas
                  onClick={() => setOpen({ kind: 'close' })}
                  testId="assessment-close"
                >
                  {w.close(assessment.label)}
                </Menu.Item>
                {withdrawable ? (
                  <Menu.Item
                    saas
                    onClick={() => setOpen({ kind: 'withdraw' })}
                    testId="assessment-withdraw"
                  >
                    {w.withdraw(assessment.label)}
                  </Menu.Item>
                ) : null}
              </Menu.Content>
            </Menu.Root>
          </div>
        ) : null}
      </div>

      {/* The one count, in words: a bar adds weight, not a fact (the annual disclosures' rule). */}
      <p
        className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
        data-testid="assessment-count"
      >
        <span className="font-w600 text-text-strong">
          {w.answeredOf(assessment.answered_count, assessment.asked_count)}
        </span>{' '}
        {w.answeredWord}
      </p>

      <Card className="overflow-hidden" testId="assessment-people">
        <PeopleTable
          orgRef={orgRef}
          assessment={assessment}
          canAct={canAct}
          sheetsWaiting={waiting}
          setOpen={setOpen}
        />
      </Card>

      {open?.kind === 'send' ? (
        <SendDialog
          orgRef={orgRef}
          assessment={assessment}
          people={open.people}
          organization={organization}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'remind' ? (
        <RemindDialog
          orgRef={orgRef}
          assessment={assessment}
          people={open.people}
          organization={organization}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'close' ? (
        <CloseDialog
          orgRef={orgRef}
          assessment={assessment}
          onClose={() => setOpen(null)}
          onClosed={(id) => {
            setOpen(null);
            void navigate({ to: BOARD_PAGES.evaluations, search: { assessment: id } });
          }}
        />
      ) : null}
      {open?.kind === 'withdraw' ? (
        <WithdrawDialog orgRef={orgRef} assessment={assessment} onClose={() => setOpen(null)} />
      ) : null}
      {open?.kind === 'sheet' ? (
        <PaperSheetDialog orgRef={orgRef} assessment={assessment} onClose={() => setOpen(null)} />
      ) : null}
      {sheet}
    </section>
  );
}

/** Each person asked, and each one the roster added since: their standing, and the row's acts. */
function PeopleTable({
  orgRef,
  assessment,
  canAct,
  sheetsWaiting: waiting,
  setOpen,
}: {
  orgRef: string;
  assessment: BoardSelfAssessmentOut;
  canAct: boolean;
  sheetsWaiting: number;
  setOpen: (next: Open) => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const mark = useMutation({
    mutationFn: async ({
      person,
      marked,
    }: {
      person: BoardSelfAssessmentPersonOut;
      marked: boolean;
    }) => {
      const options = {
        path: {
          org_ref: orgRef,
          assessment_id: assessment.id,
          board_member_id: person.board_member_id,
        },
        throwOnError: true as const,
      };
      if (marked) await operations.officerMarkBoardSelfAssessmentPaperHandIn(options);
      else await operations.officerUnmarkBoardSelfAssessmentPaperHandIn(options);
    },
    onSuccess: async (_data, { person, marked }) => {
      const name = person.display_name ?? '';
      toast.success(marked ? w.markedPaper(name) : w.unmarkedPaper(name));
      await refresh();
    },
    onError: (caught) =>
      toast.error(isGivmoError(caught) && caught.message.trim() ? caught.message : w.couldNotSave),
  });

  const columns: DataTableColumn<BoardSelfAssessmentPersonOut>[] = [
    {
      id: 'person',
      header: w.personColumn,
      cell: (row) => <PersonCell name={row.display_name ?? ''} capacity={personCapacity(row)} />,
      stacked: 'heading',
    },
    {
      id: 'status',
      header: w.statusColumn,
      cell: (row) => {
        const status = personStatus(row);
        return (
          <span className="flex flex-col items-start gap-2">
            <StatusWord tone={status.tone} testId={`assessment-status-${row.board_member_id}`}>
              {status.word}
            </StatusWord>
            {status.notes.map((note) => (
              <span
                key={note}
                className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
              >
                {note}
              </span>
            ))}
          </span>
        );
      },
      width: '248px',
      stacked: 'line',
      stackedClassName: 'max-sm:mt-6',
    },
    ...(canAct
      ? [
          {
            id: 'act',
            header: <span className="sr-only">{w.actionsColumn}</span>,
            cell: (row: BoardSelfAssessmentPersonOut) => {
              const name = row.display_name ?? '';
              const marked = Boolean(row.handed_in_on_paper_at);
              const actions = [
                ...(row.can_remind
                  ? [
                      {
                        label: w.remind,
                        onSelect: () => setOpen({ kind: 'remind', people: [row] }),
                      },
                    ]
                  : []),
                ...(row.can_mark_paper
                  ? [
                      {
                        label: w.markPaper,
                        onSelect: () => mark.mutate({ person: row, marked: true }),
                      },
                    ]
                  : []),
                // An undo is offered while a mark waits for its sheet: the backend refuses one that
                // would leave fewer marks than sheets (marks before sheets, §4.3).
                ...(marked && waiting > 0
                  ? [
                      {
                        label: w.unmarkPaper,
                        onSelect: () => mark.mutate({ person: row, marked: false }),
                      },
                    ]
                  : []),
              ];
              return (
                <span className="flex items-center justify-end gap-6 max-sm:absolute max-sm:right-8 max-sm:top-8">
                  {row.can_send ? (
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      leadingIcon={<Icon icon={Send} size={14} aria-hidden />}
                      onClick={() => setOpen({ kind: 'send', people: [row] })}
                      testId={`assessment-send-${row.board_member_id}`}
                    >
                      {w.send}
                    </Button>
                  ) : null}
                  {actions.length > 0 ? (
                    <RowActions
                      testId={`assessment-actions-${row.board_member_id}`}
                      label={w.actionsFor(name)}
                      actions={actions}
                    />
                  ) : null}
                </span>
              );
            },
            width: '160px',
            align: 'right' as const,
            stacked: 'line' as const,
          },
        ]
      : []),
  ];

  return (
    <DataTable
      columns={columns}
      rows={assessment.people}
      rowKey={(row) => row.board_member_id}
      rowTestId={(row) => `assessment-row-${row.board_member_id}`}
      stackAt="sm"
    />
  );
}

/** A closed one: its results and the board's priorities (s3). */
function ResultsView({ orgRef, assessmentId }: { orgRef: string; assessmentId: string }) {
  const canAct = useBoardPermission(ASSESSMENT_ACT_PERMISSION);
  const resultsQuery = useSelfAssessmentResults(orgRef, assessmentId);
  // The dashboard's read carries the one served sentence about each person's answers.
  const dashboard = useSelfAssessment(orgRef, assessmentId).data ?? null;
  const results = resultsQuery.data ?? null;
  const [linking, setLinking] = useState(false);

  let body;
  if (resultsQuery.isPending) {
    body = <Skeleton className="h-84 w-full rounded-lg" testId="assessment-results-loading" />;
  } else if (results === null) {
    body = (
      <ReadFailed onRetry={() => void resultsQuery.refetch()} testId="assessment-results-error" />
    );
  } else {
    const linked = results.priorities !== null && results.priorities !== undefined;
    body = (
      <AssessmentResults
        results={results}
        testId="assessment-results"
        answersStatement={dashboard?.answers_statement ?? null}
        recordLink={(children) => (
          <Link
            to={BOARD_PAGES.minuteBook + '/$recordId'}
            params={{ recordId: results.record.record_id }}
            className={INLINE_LINK}
            data-testid="assessment-results-record"
          >
            {children}
          </Link>
        )}
        headAction={
          canAct && !linked ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => setLinking(true)}
              testId="assessment-priorities-open"
            >
              {w.recordPriorities}
            </Button>
          ) : null
        }
        prioritiesAction={
          canAct && linked ? (
            <button
              type="button"
              className={`${SMALL_LINK} self-start`}
              onClick={() => setLinking(true)}
              data-testid="assessment-priorities-change"
            >
              {w.changePriorities}
            </button>
          ) : null
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-16" data-testid="assessment-results-view">
      <BackToAll />
      {body}
      {linking && results !== null ? (
        <PrioritiesDialog
          orgRef={orgRef}
          assessmentId={assessmentId}
          results={results}
          onClose={() => setLinking(false)}
        />
      ) : null}
    </div>
  );
}
